/**
 * NP 後払い確認画面のロジックを管理するフック
 *
 * - usePaymentInfo で見積もり/支払い情報を取得
 * - account 情報を取得し email の初期値を提示
 * - email 入力値の必須/形式バリデーション
 * - 税抜 → 手数料加算 → 消費税の順で税込合計を計算
 * - 同意チェック後の handlePayment で:
 *     1. email が account.email と異なれば PUT /accounts/:id で更新
 *     2. createPayment API を呼び、レスポンスの redirect URL に遷移
 *
 * 設計補足: API は二段階 (NetProtections の execTransaction + execTransactionSettle)
 * をバックエンド側で完結させ、フロントには成功/失敗の redirect URL のみ返す
 *
 * email 仕様（PO 要件）:
 *   - account.email があれば初期値として埋める / 無ければ空欄
 *   - 必須・形式バリデーション通過時のみ「審査へ進む」活性化
 *   - 入力値が account.email と完全一致なら更新スキップ（無駄な API 呼び出し抑止）
 *   - account.email を上書きする副作用あり（プロフィール画面と同じ email になる）
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { appBridge } from '../../../shared/services/bridge';

import {
  accountRepository,
  type Account,
} from '../../account/data/accountRepository';
import { paymentRepository, type PaymentOrEstimateInfo } from '../data/paymentRepository';
import { usePaymentInfo } from './usePaymentInfo';

export interface PostpaidAmount {
  /** 税抜金額（見積もり合計） */
  price: number;
  /** 消費税率（パーセント） */
  taxRate: number;
  /** 手数料率（パーセント） */
  commissionRate: number;
  /** 事務手数料額 */
  commissionAmount: number;
  /** 消費税額 */
  taxAmount: number;
  /** 税込合計 */
  totalAmount: number;
}

/**
 * 税抜金額・手数料率・税率から後払いに必要な金額内訳を計算する pure 関数
 *
 * Doctor 側 usePostpaidPaymentConfirm および NetProtections SDK の amount 計算と整合させる:
 * 1. 事務手数料 = floor(price * commissionRate / 100)
 * 2. 消費税 = floor((price + commissionAmount) * taxRate / 100)
 * 3. 税込 = price + commissionAmount + taxAmount
 */
export const calculatePostpaidAmount = (
  price: number,
  taxRate: number,
  commissionRate: number,
): PostpaidAmount => {
  const commissionAmount = Math.floor((price * commissionRate) / 100);
  const subtotalWithCommission = price + commissionAmount;
  const taxAmount = Math.floor((subtotalWithCommission * taxRate) / 100);
  const totalAmount = subtotalWithCommission + taxAmount;
  return { price, taxRate, commissionRate, commissionAmount, taxAmount, totalAmount };
};

/**
 * email 形式の簡易バリデーション
 * - 標準的な x@y.z 形式
 * - サーバー側で再度バリデーションされる前提のため厳密性は最低限
 */
export const isValidEmail = (value: string): boolean => {
  if (!value) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

interface UsePostpaidPaymentConfirmResult {
  paymentData: PaymentOrEstimateInfo;
  amount: PostpaidAmount | null;
  isLoading: boolean;
  /** 決済情報取得失敗時のエラー（画面全体を覆うエラー表示用）*/
  error: string | null;
  isAgreed: boolean;
  isSubmitting: boolean;
  /** email 入力値 */
  email: string;
  /** email がフォーカス離脱済みか（エラー表示制御に使用） */
  emailTouched: boolean;
  /** email バリデーションメッセージ（フォーカス離脱後のみ表示）*/
  emailError: string | null;
  /** account 更新失敗時のエラー（email 入力欄付近にインライン表示用）*/
  emailUpdateError: string | null;
  /** email 入力ハンドラ */
  setEmail: (value: string) => void;
  /** email フォーカス離脱ハンドラ */
  onEmailBlur: () => void;
  /** 同意チェック・email 形式・account 取得完了が揃った時のみ true */
  canSubmit: boolean;
  toggleAgreed: () => void;
  handlePayment: () => Promise<void>;
}

export const usePostpaidPaymentConfirm = (
  inquiryId: string,
  accountId: string | undefined,
  token?: string,
): UsePostpaidPaymentConfirmResult => {
  const navigate = useNavigate();
  const { data: paymentData, isLoading: isPaymentLoading, error: paymentError } =
    usePaymentInfo(inquiryId, token);

  const [account, setAccount] = useState<Account | null>(null);
  // accountId 指定時に GET /accounts のレスポンス待ちか
  // 「審査へ進む」を待たせるためだけに使用。画面の isLoading には含めない。
  const [isAccountFetching, setIsAccountFetching] = useState<boolean>(false);
  const [accountError, setAccountError] = useState<string | null>(null);

  const [email, setEmailState] = useState<string>('');
  // ユーザーが email を編集したかのフラグ（useRef）。
  // useEffect の依存に含めずに最新値を参照するため。GET /accounts が遅延した時に
  // 既に手入力された email を上書きしないために使う。
  const emailUserEditedRef = useRef<boolean>(false);
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  // account 更新失敗時のエラー。決済情報取得失敗とは別の UI 文脈で表示するため独立 state。
  const [emailUpdateError, setEmailUpdateError] = useState<string | null>(null);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * email 入力ハンドラ。
   * ユーザー編集フラグを立てるため、生の setState を直接公開せずラップする。
   * 編集開始時に過去の account 更新エラーをクリアする。
   */
  const setEmail = useCallback((value: string) => {
    setEmailState(value);
    emailUserEditedRef.current = true;
    setEmailUpdateError(null);
  }, []);

  // アンマウント後の setState を防ぐ
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // accountId が手に入ったらアカウント情報を取得して email 初期値を埋める
  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    (async () => {
      setIsAccountFetching(true);
      setAccountError(null);
      const result = await accountRepository.getById(accountId, token);
      if (cancelled) return;
      if (result.success) {
        setAccount(result.data);
        // 既にユーザーが入力済みの値があればそれを優先（API レスポンス遅延ガード）。
        // ただし emailUserEditedRef は autoComplete のブラウザ autofill でも true になり、
        // 真の意味で「ユーザーが入力したか」を判別できない問題がある。
        // よって state の現在値が空文字（=未入力 or autofill 起因の意図せぬ空）の場合は
        // account.email で埋め直す。
        setEmailState(prev => (prev === '' ? (result.data.email ?? '') : prev));
      } else {
        setAccountError(result.error.message);
      }
      setIsAccountFetching(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, token]);

  const amount: PostpaidAmount | null = useMemo(() => {
    if (!paymentData) return null;
    return calculatePostpaidAmount(
      paymentData.price ?? 0,
      paymentData.taxRate ?? 0,
      paymentData.commissionRate ?? 0,
    );
  }, [paymentData]);

  const toggleAgreed = useCallback(() => {
    setIsAgreed(prev => !prev);
  }, []);

  const onEmailBlur = useCallback(() => {
    setEmailTouched(true);
  }, []);

  const emailError = useMemo(() => {
    if (!emailTouched) return null;
    if (!email.trim()) return 'メールアドレスを入力してください';
    if (!isValidEmail(email)) return 'メールアドレスの形式が正しくありません';
    return null;
  }, [email, emailTouched]);

  // canSubmit:
  // - 同意 + email 形式 OK + 送信中でない
  // - accountId 指定時は account 取得待ちでないこと（race condition による PUT スキップを防ぐ）
  const canSubmit =
    isAgreed &&
    isValidEmail(email) &&
    !isSubmitting &&
    (!accountId || (account !== null && !isAccountFetching));

  const handlePayment = useCallback(async () => {
    if (!inquiryId || !paymentData || !canSubmit) return;

    setIsSubmitting(true);
    appBridge.showLoading();

    // PaymentFailure が inquiry_id を引き当てるための退避
    sessionStorage.setItem('payment_inquiry_id', inquiryId);

    try {
      // 1. email が account.email と異なれば account 更新
      // accountId が無い / account 取得失敗の場合は更新せず先に進む（既存 account.email が使われる）
      const trimmedEmail = email.trim();
      if (accountId && account && trimmedEmail !== (account.email ?? '')) {
        const updateResult = await accountRepository.update(
          accountId,
          {
            name: account.name,
            nameKana: account.nameKana,
            genderId: account.genderId,
            mobilePhoneNumber: account.mobilePhoneNumber,
            landlinePhoneNumber: account.landlinePhoneNumber ?? null,
            email: trimmedEmail,
            birthday: account.birthday ?? null,
          },
          token,
        );
        if (!isMountedRef.current) return;
        if (!updateResult.success) {
          // 更新失敗時は決済に進まずエラー表示（NetProtections に旧 email が送られて
          // 与信 NG になるのを避ける）
          // eslint-disable-next-line no-console
          console.error(
            '[usePostpaidPaymentConfirm] account update failed:',
            updateResult.error.message,
          );
          appBridge.hideLoading();
          setEmailUpdateError('メールアドレスの更新に失敗しました。再度お試しください。');
          return;
        }
      }

      // 2. createPayment 実行
      // API側 paymentAll.ts schema で postpaid: { email } が必須のため必ず送信する。
      // 上記 PUT /accounts は account.email を最新化するための副作用、
      // postpaid.email は本リクエストでバリデーション/与信に使われる主データ。
      const result = await paymentRepository.createPayment(
        {
          inquiryId,
          paymentMethodCode: 'postpaid',
          price: paymentData.price ?? 0,
          taxRate: paymentData.taxRate ?? 0,
          postpaid: { email: trimmedEmail },
        },
        token,
      );

      if (!isMountedRef.current) return;
      appBridge.hideLoading();

      if (result.success && result.data.redirect) {
        window.location.href = result.data.redirect;
      } else {
        const errorMessage = !result.success
          ? result.error.message
          : 'No redirect URL in response';
        // eslint-disable-next-line no-console
        console.error('[usePostpaidPaymentConfirm] createPayment failed:', errorMessage);
        navigate(`/inquiries/${inquiryId}/payment-failed`);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        '[usePostpaidPaymentConfirm] handlePayment threw:',
        e instanceof Error ? `${e.name}: ${e.message}` : String(e),
      );
      if (!isMountedRef.current) return;
      appBridge.hideLoading();
      navigate(`/inquiries/${inquiryId}/payment-failed`);
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [inquiryId, paymentData, canSubmit, accountId, account, email, token, navigate]);

  return {
    paymentData,
    amount,
    // account 取得は画面の描画を待たせない（email は取得完了後に setState で埋まる）
    // isAccountLoading は内部状態として使うのみ。isAccountLoading を画面ローディングに混ぜると
    // accountId が手に入ってから一瞬画面が消える挙動になるため意図的に除外。
    isLoading: isPaymentLoading,
    // error は決済情報取得失敗（画面全体エラー）のみ。
    // accountError は画面全体を覆わず、email 入力欄付近に emailUpdateError と
    // 同じ場所で表示する（後述 emailUpdateError に統合済み）。
    error: paymentError,
    isAgreed,
    isSubmitting,
    email,
    emailTouched,
    emailError,
    // emailUpdateError (PUT 失敗) と accountError (GET 失敗) を統合して
    // email 入力欄付近に 1 つの場所で表示する
    emailUpdateError: emailUpdateError || accountError,
    setEmail,
    onEmailBlur,
    canSubmit,
    toggleAgreed,
    handlePayment,
  };
};
